import AuthorizedApiClient from 'modules/auth/models/authorizedApiClient';
import { AxiosResponse } from 'axios';
import environment from '@/environment';
import {
  Survey,
  SurveyAnswers,
  SurveySubmissionResponse,
  AvailableSurveyResponse,
  SurveyQuestionsResponse,
  SurveySubmitResponse,
  SurveyDismissResponse,
} from '../interfaces/survey';

export class SurveyApi {
  /**
   * Transform form builder fields to questions format
   */
  private transformFieldsToQuestions(fields: any[]): any[] {
    if (!fields || !Array.isArray(fields)) {
      return [];
    }

    return fields.map((field) => ({
      qid: String(field.id || field.fieldId || Math.random()), // Use field ID as qid
      type: field.fieldType,
      text: field.label,
      required: field.required || false,
      options: field.options || null,
      validation: field.validation || null,
    }));
  }

  /**
   * Get available survey for the current user
   * @returns Promise with survey data or null if no survey available
   */
  public async getAvailableSurvey(): Promise<AvailableSurveyResponse> {
    try {
      const response: AxiosResponse<AvailableSurveyResponse> = await AuthorizedApiClient.get(
        `${environment.backendUrl}${environment.apiPrefix}/survey/available`,
      );

      if (response.status === 200) {
        const apiResponse = response.data;
        // Handle case where no survey is available (not an error)
        if (apiResponse.success && !apiResponse.data) {
          return {
            success: true,
            statusCode: 200,
            message: null,
            data: null,
          };
        }

        // Transform form builder fields to questions if needed
        if (apiResponse.success && apiResponse.data) {
          const data = apiResponse.data;
          
          // Handle array of surveys
          if (Array.isArray(data)) {
            const transformedSurveys = data.map((surveyData: any) => {
              // If the survey has fields but no questions, transform fields to questions
              if (surveyData.fields && Array.isArray(surveyData.fields) && (!surveyData.questions || surveyData.questions.length === 0)) {
                surveyData.questions = this.transformFieldsToQuestions(surveyData.fields);
                // Remove fields from the response to avoid confusion
                delete surveyData.fields;
              }
              return surveyData;
            });
            
            return {
              ...apiResponse,
              data: transformedSurveys,
            };
          } else {
            // Handle single survey (backward compatibility)
            const surveyData = data as any;
            
            // If the response has fields but no questions, transform fields to questions
            if (surveyData.fields && Array.isArray(surveyData.fields) && (!surveyData.questions || surveyData.questions.length === 0)) {
              surveyData.questions = this.transformFieldsToQuestions(surveyData.fields);
              // Remove fields from the response to avoid confusion
              delete surveyData.fields;
            }
          }
        }

        return apiResponse;
      }

      return {
        success: false,
        statusCode: response.status,
        message: 'Failed to fetch survey',
        data: null,
      };
    } catch (error: any) {
      // Handle case where no survey is available (404 or similar)
      if (error.response?.status === 200 && !error.response?.data?.data) {
        return {
          success: true,
          statusCode: 200,
          message: null,
          data: null,
        };
      }

      throw error;
    }
  }

  /**
   * Get survey questions for a specific survey
   * @param surveyId - The survey ID
   * @returns Promise with array of questions
   */
  public async getSurveyQuestions(surveyId: number): Promise<SurveyQuestionsResponse> {
    const response: AxiosResponse<SurveyQuestionsResponse> = await AuthorizedApiClient.get(
      `${environment.backendUrl}${environment.apiPrefix}/survey/${surveyId}/questions`,
    );

    return response.data;
  }

  /**
   * Submit survey response
   * @param surveyId - The survey ID
   * @param answers - Object mapping question IDs to answers
   * @param isAnonymous - Optional anonymity override for review questions
   * @returns Promise with submission response
   */
  public async submitSurveyResponse(
    surveyId: number,
    answers: SurveyAnswers,
    isAnonymous?: boolean | null,
  ): Promise<SurveySubmitResponse> {
    const body: { answers: SurveyAnswers; isAnonymous?: boolean } = { answers };
    if (isAnonymous !== undefined && isAnonymous !== null) {
      body.isAnonymous = isAnonymous;
    }
    const response: AxiosResponse<SurveySubmitResponse> = await AuthorizedApiClient.post(
      `${environment.backendUrl}${environment.apiPrefix}/survey/${surveyId}/submit`,
      body,
    );

    return response.data;
  }

  /**
   * Mark survey as shown to user (creates pending response)
   * @param surveyId - The survey ID
   * @returns Promise with response
   */
  public async markSurveyShown(surveyId: number): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await AuthorizedApiClient.post(
      `${environment.backendUrl}${environment.apiPrefix}/survey/${surveyId}/shown`,
    );

    return response.data;
  }

  /**
   * Dismiss a survey (skip without answering)
   * @param surveyId - The survey ID
   * @returns Promise with dismissal response
   */
  public async dismissSurvey(surveyId: number): Promise<SurveyDismissResponse> {
    const response: AxiosResponse<SurveyDismissResponse> = await AuthorizedApiClient.post(
      `${environment.backendUrl}${environment.apiPrefix}/survey/${surveyId}/dismiss`,
    );

    return response.data;
  }
}
